import React, { useEffect, useRef, useCallback, useState } from "react";
import { Form, Input, Select, DatePicker, Row, Col } from "antd";
import { CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useLocationOptions from "../../../hooks/useLocationOptions";
import RoleSelectWithOther from "../../../components/RoleSelectWithOther";
import { API_CONST } from "../../../const";

const { Option } = Select;

const GENDER_OPTIONS = ["Male", "Female", "Non-binary", "Prefer not to say"];

const calcAge = (dob) => {
  if (!dob) return null;
  const d = dayjs(dob);
  if (!d.isValid()) return null;
  return dayjs().diff(d, "year");
};

const BasicDetails = ({ initialData, onValidate }) => {
  const [form] = Form.useForm();
  const [pricingNudge, setPricingNudge] = useState(null);
  const [age, setAge] = useState(null);
  const nudgeTimer = useRef(null);
  const { countries, cities, fetchCities } = useLocationOptions(initialData?.country || "India");

  useEffect(() => {
    const values = { country: "India", ...initialData };
    if (values.dob && typeof values.dob === "string") {
      values.dob = dayjs(values.dob);
      setAge(calcAge(initialData.dob));
    }
    if (values.rate === undefined || values.rate === null) values.rate = "";
    if (values.country) fetchCities(values.country);
    form.setFieldsValue(values);
  }, [initialData, form]);

  useEffect(() => {
    onValidate(() => form.validateFields());
  }, [form, onValidate]);

  const checkPricing = useCallback(async (rate, role, city, experienceYears, skills, country) => {
    if (!rate || !role) { setPricingNudge(null); return; }
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(API_CONST.ROLE_PRICING_CHECK, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ roleName: role, city, country: country || "India", experienceYears: Number(experienceYears) || 0, rate: Number(rate), skills }),
      });
      const data = await res.json();
      setPricingNudge(data.status ? data : null);
    } catch { setPricingNudge(null); }
  }, []);

  const triggerPricingCheck = useCallback(() => {
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    nudgeTimer.current = setTimeout(() => {
      const v = form.getFieldsValue();
      checkPricing(v.rate, v.role, v.location, v.experienceYears, v.primarySkills, v.country);
    }, 600);
  }, [form, checkPricing]);

  useEffect(() => () => { if (nudgeTimer.current) clearTimeout(nudgeTimer.current); }, []);

  const handleCountryChange = (country) => {
    fetchCities(country);
    form.setFieldsValue({ location: undefined });
    triggerPricingCheck();
  };

  const handleDobChange = (date) => {
    setAge(date ? calcAge(date.format("YYYY-MM-DD")) : null);
  };

  const renderNudge = () => {
    if (!pricingNudge || pricingNudge.status === "unknown") return null;
    const cfg = {
      above: { color: "#cf1322", icon: <ExclamationCircleOutlined />, text: "Rate is above the market benchmark. You can still continue." },
      below: { color: "#d48806", icon: <ExclamationCircleOutlined />, text: "Rate is below the market benchmark. You can still continue." },
      within: { color: "#389e0d", icon: <CheckCircleOutlined />, text: "Rate is within the market benchmark range." },
    }[pricingNudge.status];
    if (!cfg) return null;
    return (
      <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, color: cfg.color, fontSize: 12 }}>
        {cfg.icon}<span>{cfg.text}</span>
      </div>
    );
  };


  return (
    <Form form={form} layout="vertical" initialValues={{ country: "India" }}>
      <p className="section-title">Personal Information</p>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Full Name" name="name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="Enter full name" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Gender" name="gender">
            <Select placeholder="Select gender" allowClear>
              {GENDER_OPTIONS.map((g) => <Option key={g} value={g}>{g}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Date of Birth" name="dob">
            <DatePicker
              style={{ width: "100%" }}
              placeholder="YYYY-MM-DD"
              disabledDate={(d) => d && d.isAfter(dayjs())}
              onChange={handleDobChange}
            />
          </Form.Item>
          {age !== null && (
            <div style={{ marginTop: -14, marginBottom: 8, fontSize: 12, color: "#01d9a9", fontWeight: 600 }}>
              Age: {age} years
            </div>
          )}
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Email" name="email" rules={[{ required: true, message: "Email is required" }, { type: "email", message: "Enter a valid email" }]}>
            <Input placeholder="Enter email address" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Phone" name="phone" rules={[{ required: true, message: "Phone is required" }]}>
            <Input placeholder="Enter phone number" />
          </Form.Item>
        </Col>
      </Row>

      <p className="section-title" style={{ marginTop: 4 }}>Role & Location</p>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Role" name="role" rules={[{ required: true, message: "Role is required" }]}>
            <RoleSelectWithOther source="partner" placeholder="Select or enter role" onAfterChange={triggerPricingCheck} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Country" name="country" rules={[{ required: true, message: "Country is required" }]}>
            <Select placeholder="Select country" showSearch optionFilterProp="children" onChange={handleCountryChange}>
              {countries.map((c) => <Option key={c.value} value={c.value}>{c.label}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="City" name="location" rules={[{ required: true, message: "City is required" }]}>
            <Select placeholder="Select city" showSearch optionFilterProp="children" onChange={triggerPricingCheck}>
              {cities.map((c) => <Option key={c.value} value={c.value}>{c.label}</Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Monthly Rate (₹)"
            name="rate"
            rules={[{ required: true, message: "Monthly rate is required" }]}
            extra={renderNudge()}
          >
            <Input prefix="₹" placeholder="e.g. 80000" onChange={triggerPricingCheck} onBlur={triggerPricingCheck} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default BasicDetails;
